import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  DotsThreeVertical, PencilSimple, Trash, Eye, Copy, ArrowUUpLeft, ClockCounterClockwise
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

const FIELDS = [
  ["code", "SKU Code", "text"],
  ["name", "Product Name", "text"],
  ["category", "Category", "text"],
  ["variant", "Variant", "select", ["Regular", "Jain"]],
  ["net_weight_g", "Net Weight (g)", "number"],
  ["shelf_life_days", "Shelf Life (days)", "number"],
  ["dry_yield_pct", "Dry Yield %", "number"],
  ["cost_per_unit", "Raw Cost / pouch", "number"],
  ["labor_cost_per_unit", "Labour / pouch", "number"],
  ["machine_cost_per_unit", "Machine / pouch", "number"],
  ["electricity_cost_per_unit", "Electricity / pouch", "number"],
  ["overhead_cost_per_unit", "Overhead / pouch", "number"],
  ["selling_price", "Selling Price", "number"],
  ["packaging_details", "Packaging Details", "textarea"],
  ["manufacturing_steps", "Mfg Steps", "textarea"],
  ["status", "Status", "select", ["active", "inactive"]],
];

// ---------- Actions dropdown (used in SKU table row) ----------
export function SkuActions({ sku, onEdit, onView, onDuplicate, onDelete, onRestore, currentUser }) {
  const role = currentUser?.role;
  const canEdit = ["admin", "production_manager"].includes(role);
  const canDelete = role === "admin";
  const canRestore = role === "admin";
  const isDeleted = sku.status === "deleted";
  return (
    <div className="flex items-center gap-0.5 justify-end">
      {/* Desktop icon buttons — hidden on small screens */}
      <div className="hidden md:flex items-center gap-0.5">
        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onView(sku)}
                title="View" data-testid={`sku-view-${sku.code}`}>
          <Eye size={15}/>
        </Button>
        {canEdit && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onEdit(sku)}
                  title="Edit" data-testid={`sku-edit-${sku.code}`}>
            <PencilSimple size={15}/>
          </Button>
        )}
        {canEdit && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => onDuplicate(sku)}
                  title="Duplicate" data-testid={`sku-duplicate-${sku.code}`}>
            <Copy size={15}/>
          </Button>
        )}
        {canDelete && !isDeleted && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onDelete(sku)} title="Delete" data-testid={`sku-delete-${sku.code}`}>
            <Trash size={15}/>
          </Button>
        )}
        {canRestore && isDeleted && (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => onRestore(sku)} title="Restore" data-testid={`sku-restore-${sku.code}`}>
            <ArrowUUpLeft size={15}/>
          </Button>
        )}
      </div>
      {/* Mobile 3-dot menu */}
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`sku-actions-${sku.code}`}>
              <DotsThreeVertical size={16} weight="bold"/>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onView(sku)}><Eye size={14} className="mr-2"/>View</DropdownMenuItem>
            {canEdit && <DropdownMenuItem onSelect={() => onEdit(sku)}><PencilSimple size={14} className="mr-2"/>Edit</DropdownMenuItem>}
            {canEdit && <DropdownMenuItem onSelect={() => onDuplicate(sku)}><Copy size={14} className="mr-2"/>Duplicate</DropdownMenuItem>}
            <DropdownMenuSeparator/>
            {canDelete && !isDeleted && (
              <DropdownMenuItem onSelect={() => onDelete(sku)} className="text-red-600">
                <Trash size={14} className="mr-2"/>Delete
              </DropdownMenuItem>
            )}
            {canRestore && isDeleted && (
              <DropdownMenuItem onSelect={() => onRestore(sku)} className="text-emerald-600">
                <ArrowUUpLeft size={14} className="mr-2"/>Restore
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ---------- Edit / View drawer ----------
export function SkuEditDialog({ open, onOpenChange, sku, mode = "edit", onSaved }) {
  const [form, setForm] = useState({});
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const readOnly = mode === "view";

  useEffect(() => {
    if (!open || !sku) return;
    setForm({ ...sku });
    api.get(`/skus/${sku.id}/audit`).then(r => setAudit(r.data)).catch(() => setAudit([]));
  }, [open, sku]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const patch = { ...form };
      delete patch.id;
      await api.put(`/skus/${sku.id}`, patch);
      toast.success("SKU updated successfully.");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {readOnly ? <><Eye size={17}/>View SKU</> : <><PencilSimple size={17}/>Edit SKU</>}
            <span className="mono text-xs font-normal text-muted-foreground">· {sku?.code}</span>
            {sku?.status && sku.status !== "active" && (
              <Badge variant="secondary" className="ml-1 uppercase">{sku.status}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {readOnly ? "Read-only view of this SKU's recipe, cost breakdown and version history."
                       : "Update the SKU details. Changes are audit-logged with your name and timestamp."}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details" data-testid="sku-tab-details">Details</TabsTrigger>
            <TabsTrigger value="bom" data-testid="sku-tab-bom">BOM / Ingredients ({(sku?.ingredients||[]).length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="sku-tab-audit"><ClockCounterClockwise size={13} className="mr-1"/>Version history ({audit.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map(([k, label, type, options]) => (
                <div key={k} className={type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label className="overline text-[9px]">{label}</Label>
                  {type === "select" ? (
                    <Select value={String(form[k] ?? "")} onValueChange={(v) => set(k, v)} disabled={readOnly}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`sku-field-${k}`}>
                        <SelectValue placeholder="—"/>
                      </SelectTrigger>
                      <SelectContent>
                        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : type === "textarea" ? (
                    <Textarea value={form[k] ?? ""} onChange={e => set(k, e.target.value)}
                              disabled={readOnly} rows={2} className="text-sm"
                              data-testid={`sku-field-${k}`}/>
                  ) : (
                    <Input value={form[k] ?? ""} onChange={e => set(k, type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
                           disabled={readOnly} type={type === "number" ? "number" : "text"}
                           className={`h-9 text-sm ${type === "number" ? "mono" : ""}`}
                           data-testid={`sku-field-${k}`}/>
                  )}
                </div>
              ))}
            </div>
            {(() => {
              const tc = (form.cost_per_unit||0)+(form.labor_cost_per_unit||0)+(form.machine_cost_per_unit||0)+(form.electricity_cost_per_unit||0)+(form.overhead_cost_per_unit||0);
              const margin = (form.selling_price||0) - tc;
              return (
                <div className="mt-4 p-3 rounded-md border border-border bg-muted/30 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Cost / pouch</span>
                  <span className="mono font-bold">₹{tc.toFixed(2)}</span>
                  <span className="text-muted-foreground">→ Margin</span>
                  <span className={`mono font-bold ${margin >= 0 ? "text-emerald-600" : "text-red-600"}`}>₹{margin.toFixed(2)}</span>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="bom" className="mt-3">
            {(sku?.ingredients || []).length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No BOM defined for this SKU.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 text-xs uppercase text-muted-foreground font-semibold">Material</th>
                    <th className="text-right py-2 text-xs uppercase text-muted-foreground font-semibold">Qty / pouch</th>
                    <th className="text-right py-2 text-xs uppercase text-muted-foreground font-semibold">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {sku.ingredients.map((ing, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0">
                      <td className="py-2 font-medium">{ing.name || <span className="text-muted-foreground italic">unknown</span>}</td>
                      <td className="py-2 text-right mono">{ing.qty_g || ing.qty || 0}</td>
                      <td className="py-2 text-right mono text-muted-foreground">{ing.unit || "g"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TabsContent>

          <TabsContent value="audit" className="mt-3">
            {audit.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No changes recorded yet.</div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-auto">
                {audit.map(a => (
                  <div key={a.id} className="border border-border rounded-md p-3 text-xs" data-testid={`audit-${a.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold uppercase text-[10px] tracking-wider">{a.action.replace("_", " ")}</span>
                      <span className="mono text-muted-foreground">{new Date(a.at).toLocaleString()}</span>
                    </div>
                    <div className="mb-1 text-muted-foreground">by {a.user} · {a.role}</div>
                    {(a.diff && a.diff.length > 0) && (
                      <div className="grid grid-cols-1 gap-1 mt-1.5">
                        {a.diff.slice(0, 8).map((d, i) => (
                          <div key={i} className="flex items-baseline gap-2 rounded bg-muted/40 px-2 py-1">
                            <span className="mono text-[10px] font-semibold whitespace-nowrap">{d.field}</span>
                            <span className="text-red-600 line-through truncate">{String(d.before ?? "—").slice(0, 40)}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className="text-emerald-600 truncate">{String(d.after ?? "—").slice(0, 40)}</span>
                          </div>
                        ))}
                        {a.diff.length > 8 && <div className="text-[10px] text-muted-foreground">+ {a.diff.length - 8} more field changes</div>}
                      </div>
                    )}
                    {a.usage && a.usage.total > 0 && (
                      <div className="mt-1 text-[10px] text-amber-700">Usage at the time: {a.usage.total} refs across production history</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{readOnly ? "Close" : "Cancel"}</Button>
          {!readOnly && (
            <Button onClick={save} disabled={busy} data-testid="sku-save">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Duplicate dialog ----------
export function SkuDuplicateDialog({ open, onOpenChange, sku, onDone }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !sku) return;
    setCode(`${sku.code}-COPY`);
    setName(`${sku.name} (copy)`);
  }, [open, sku]);

  const submit = async () => {
    if (!code.trim() || !name.trim()) return toast.error("Code and name are required");
    setBusy(true);
    try {
      const { data } = await api.post(`/skus/${sku.id}/duplicate`, { code: code.trim(), name: name.trim() });
      toast.success(`Duplicated as ${data.code}`);
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate SKU · {sku?.name}</DialogTitle>
          <DialogDescription>
            Copies the recipe, BOM, cost settings, SOP and packaging. You only need to set a new code and name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="overline text-[10px]">New SKU Code</Label>
            <Input value={code} onChange={e => setCode(e.target.value)} className="mono" data-testid="dup-code"/>
          </div>
          <div>
            <Label className="overline text-[10px]">New Product Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} data-testid="dup-name"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="dup-submit">
            {busy ? "Duplicating…" : "Duplicate SKU"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Delete dialog (usage-aware) ----------
export function SkuDeleteDialog({ open, onOpenChange, sku, onDone }) {
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !sku) return;
    setUsage(null);
    api.get(`/skus/${sku.id}/usage`).then(r => setUsage(r.data)).catch(() => setUsage({ total: 0 }));
  }, [open, sku]);

  const doDelete = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/skus/${sku.id}`);
      if (data.action === "marked_inactive") toast.success("SKU marked Inactive (has production history).");
      else if (data.action === "soft_deleted") toast.success("SKU moved to inactive.");
      else toast.success("SKU deleted.");
      onDone && onDone();
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const hasHistory = usage && usage.total > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600 flex items-center gap-2">
            <Trash size={18}/>{hasHistory ? "Mark SKU Inactive?" : "Delete SKU?"}
          </DialogTitle>
          <DialogDescription>
            {hasHistory
              ? "This SKU has production history and cannot be deleted. You can mark it as Inactive instead — it will disappear from active pickers but stay in the database for audit."
              : "You are about to permanently delete this SKU. This action cannot be undone."}
          </DialogDescription>
        </DialogHeader>
        {usage === null ? (
          <div className="text-sm text-muted-foreground py-2">Checking usage…</div>
        ) : hasHistory ? (
          <div className="text-xs space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="font-semibold text-amber-800 mb-1">Referenced in:</div>
            {usage.batches > 0 && <div>• {usage.batches} Production batch(es)</div>}
            {usage.finished_goods > 0 && <div>• {usage.finished_goods} Finished-goods lot(s)</div>}
            {usage.sales_orders > 0 && <div>• {usage.sales_orders} Sales order(s)</div>}
            {usage.dispatches > 0 && <div>• {usage.dispatches} Dispatch(es)</div>}
            {usage.inventory_txns > 0 && <div>• {usage.inventory_txns} Inventory transaction(s)</div>}
          </div>
        ) : (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
            No production history — safe to delete.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant={hasHistory ? "default" : "destructive"} onClick={doDelete} disabled={busy || usage === null}
                  data-testid="sku-confirm-delete">
            {busy ? "Working…" : hasHistory ? "Mark Inactive" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
